import type {
  GeneralBarDatum,
  TotalsAnnotationsValues,
} from "@jana-technology/platform-ui";
import { chartTheme, storageFormatters } from "@jana-technology/platform-ui";
import { sortBy } from "lodash-es";

import { PAGE_SIZE, REFERENCE_LINE_BASE } from "constants/charts";
import type {
  RiskComparisonConfigInput,
  RiskComparisonChartConfig,
  RiskComparisonPeerWells,
  RiskComparisonWellRanks,
} from "./types";
import { nthNumberFormatter } from "components/tables/utils/formatters";
import { TOOLTIP_SEMIBOLD } from "constants/styles";
import { findHierarchyName } from "contexts/filters";
import { RiskManagementOverallComparisonChartQuery } from "$/__generated__/graphql";

// chart data can be grouped on a nested x-axis;
export const sliceChartData = (
  chartData: Array<GeneralBarDatum>,
  activePage: number,
  groupSize = 1,
) =>
  chartData.slice(
    activePage * PAGE_SIZE * groupSize,
    (activePage + 1) * PAGE_SIZE * groupSize,
  );

// totals annotations span the group axis, so groupSize is not needed
export const annotationsSlicer = (
  totals: TotalsAnnotationsValues,
  activePage: number,
) => totals.slice(activePage * PAGE_SIZE, (activePage + 1) * PAGE_SIZE);

const splitSelectedAndPeerWells = <T extends { wellName: string }>(
  allWellsSeries: Array<T>,
  wellName: string,
  hierarchyId: number | null | undefined,
  wellNamesByHierarchy: RiskComparisonPeerWells | undefined,
) => {
  const peerWells = wellNamesByHierarchy
    ?.find((hierarchy) => hierarchy?.hierarchyId === hierarchyId)
    ?.wellNames?.filter((name) => name !== wellName);

  const selectedWellSeries = allWellsSeries.filter(
    (datum) => datum.wellName === wellName,
  );

  const otherWellsSeries = peerWells?.length
    ? allWellsSeries.filter((datum) => peerWells.includes(datum.wellName))
    : [];

  return { selectedWellSeries, otherWellsSeries };
};

const buildReferenceLines = (
  fieldMedian: number,
  systemMedian: number,
  theme: typeof chartTheme,
) => [
  {
    ...REFERENCE_LINE_BASE,
    yVal: fieldMedian,
    stroke: theme.colors[0],
    name: "Field Median",
  },
  {
    ...REFERENCE_LINE_BASE,
    yVal: systemMedian,
    stroke: theme.colors[1],
    name: "System Median",
  },
];

export const buildRiskComparisonChartConfig = (
  {
    chartData,
    wellName,
    hierarchyId,
    wellNamesByHierarchy,
    hierarchies,
    theme = chartTheme,
  }: RiskComparisonConfigInput,
  isBaseline: boolean,
  isOverall: boolean,
): RiskComparisonChartConfig => {
  if (!chartData) {
    return {
      series: [],
      referenceLines: [],
      legendDomain: [],
      legendColors: [],
      theme,
    };
  }

  const { wellRanks = [] }: { wellRanks: RiskComparisonWellRanks } = chartData;

  const allWellsSeries = wellRanks.map((well) => {
    const wellHierarchyId = wellNamesByHierarchy?.find((hierarchy) => {
      return hierarchy?.wellNames?.includes(well.wellName);
    })?.hierarchyId;

    const hierarchyName = findHierarchyName(
      wellHierarchyId ?? null,
      hierarchies ?? [],
    );

    return {
      x: well.rank,
      y: isBaseline ? well.operationalRisk : well.interventionRisk,
      label: `${well.wellName} (${hierarchyName})`,
      title: `Risk Rank - ${well.rank}${nthNumberFormatter(well.rank)}`,
      value: `${well.rank} of ${wellRanks.length}`,
      wellName: well.wellName,
      tooltipExtras: getRiskComparisonTooltipExtras(
        isBaseline,
        isOverall,
        well.operationalRisk,
        well.interventionRisk,
        well.combinedRisk,
        well?.lof,
      ),
    };
  });

  const { selectedWellSeries, otherWellsSeries } = splitSelectedAndPeerWells(
    allWellsSeries,
    wellName,
    hierarchyId,
    wellNamesByHierarchy,
  );

  const remainingColors = theme.colors.slice(0, 2);

  return {
    series: [
      {
        data: allWellsSeries,
        type: "scatter",
        id: "all-wells-ranks",
        name: "All Other Wells in System",
        size: 4,
      },
      {
        data: otherWellsSeries,
        type: "scatter",
        id: "other-wells-ranks",
        name: "Other Wells in the Same Field as Selected Well",
        size: 8,
      },
      {
        data: selectedWellSeries,
        type: "scatter",
        id: "selected-well-rank",
        name: "Selected Well",
        size: 8,
      },
    ],
    referenceLines: buildReferenceLines(
      chartData.fieldMedian,
      chartData.systemMedian,
      theme,
    ),
    legendDomain: [
      "Selected Well",
      "Other Wells in the Same Field as Selected Well",
      "All Other Wells in System",
    ],
    theme: {
      ...theme,
      colors: [
        theme.colors[10],
        theme.colors[3],
        theme.colors[2],
        ...remainingColors,
      ],
    },
    legendColors: [
      theme.colors[2],
      theme.colors[3],
      theme.colors[10],
      ...remainingColors,
    ],
  };
};

export const buildMitigationRiskComparisonChartConfig = (
  {
    chartData,
    wellName,
    hierarchyId,
    wellNamesByHierarchy,
    hierarchies,
    theme = chartTheme,
  }: Omit<RiskComparisonConfigInput, "chartData"> & {
    chartData?: RiskManagementOverallComparisonChartQuery["riskManagementOverallComparisonChart"];
  },
  riskMitigationName: string,
): RiskComparisonChartConfig => {
  if (!chartData) {
    return {
      series: [],
      referenceLines: [],
      legendDomain: [],
      legendColors: [],
      theme,
    };
  }

  const {
    mitigationWellRank = [],
  }: {
    mitigationWellRank?: RiskComparisonWellRanks;
  } = chartData;

  const sortedMitigationWellRank = sortBy(mitigationWellRank, "rank");

  const allWellsSeries = sortedMitigationWellRank.map((well) => {
    const wellHierarchyId = wellNamesByHierarchy?.find((hierarchy) => {
      return hierarchy?.wellNames?.includes(well.wellName);
    })?.hierarchyId;

    const hierarchyName = findHierarchyName(
      wellHierarchyId ?? null,
      hierarchies ?? [],
    );

    return {
      x: well.rank,
      y: well.operationalRisk,
      label: `${well.wellName} (${hierarchyName})`,
      title: `Risk Rank - ${well.rank}${nthNumberFormatter(well.rank)}`,
      value: `${well.rank} of ${sortedMitigationWellRank.length}`,
      wellName: well.wellName,
      tooltipExtras: [
        {
          label: "Mitigation",
          value: "Baseline",
          labelClassName: TOOLTIP_SEMIBOLD,
        },
        {
          label: "Baseline Operational Risk",
          value: well.operationalRisk,
        },
        {
          label: "Baseline Risk Rank",
          value: `${well.rank} of ${mitigationWellRank.length}`,
        },
        {
          label: "Post-Mitigation Operational Risk",
          value: well.postRisk,
        },
        {
          label: "Post-Mitigation Risk Rank",
          value: `${well.postRiskRank} of ${mitigationWellRank.length}`,
        },
      ],
    };
  });

  const mitigationSeries = mitigationWellRank
    ?.filter((datum) => datum.wellName === wellName)
    .map((well) => {
      const wellHierarchyId = wellNamesByHierarchy?.find((hierarchy) => {
        return hierarchy?.wellNames?.includes(well.wellName);
      })?.hierarchyId;

      const hierarchyName = findHierarchyName(
        wellHierarchyId ?? null,
        hierarchies ?? [],
      );
      return {
        x: well.postRiskRank ?? 0,
        y: well.postRisk ?? 0,
        label: `${well.wellName} (${hierarchyName})`,
        title: `Post-Mitigation Risk Rank - ${well.postRiskRank}${nthNumberFormatter(well.postRiskRank)}`,
        value: `${well.postRiskRank} of ${mitigationWellRank.length}`,
        wellName: well.wellName,
        tooltipExtras: [
          {
            label: "Mitigation",
            value: riskMitigationName,
            labelClassName: TOOLTIP_SEMIBOLD,
          },
          {
            label: "Baseline Operational Risk",
            value: well.operationalRisk,
          },
          {
            label: "Baseline Risk Rank",
            value: `${well.rank} of ${mitigationWellRank.length}`,
          },
          {
            label: "Post-Mitigation Operational Risk",
            value: well.postRisk,
          },
          {
            label: "Post-Mitigation Risk Rank",
            value: `${well.postRiskRank} of ${mitigationWellRank.length}`,
          },
        ],
      };
    });

  const { selectedWellSeries, otherWellsSeries } = splitSelectedAndPeerWells(
    allWellsSeries,
    wellName,
    hierarchyId,
    wellNamesByHierarchy,
  );

  return {
    series: [
      {
        data: allWellsSeries,
        type: "scatter",
        id: "all-wells-ranks",
        name: "All Other Wells in System",
        size: 4,
      },
      {
        data: otherWellsSeries,
        type: "scatter",
        id: "other-wells-ranks",
        name: "Other Wells in the Same Field as Selected Well",
        size: 8,
      },
      {
        data: selectedWellSeries,
        type: "scatter",
        id: "selected-well-rank",
        name: "Selected Well",
        size: 12,
      },
      {
        data: mitigationSeries,
        type: "scatter",
        id: "post-mitigation-selected-well-rank",
        name: "Post-Mitigation Risk",
        size: 8,
      },
    ],
    referenceLines: buildReferenceLines(
      chartData.fieldMedian,
      chartData.systemMedian,
      theme,
    ),
    legendDomain: [
      "Selected Well",
      "Post-Mitigation Risk",
      "Other Wells in the Same Field as Selected Well",
      "All Other Wells in System",
    ],
    theme: {
      ...theme,
      colors: [
        theme.colors[10],
        theme.colors[3],
        theme.colors[2],
        theme.colors[0],
        theme.colors[1],
      ],
    },
    legendColors: [
      theme.colors[2],
      theme.colors[0],
      theme.colors[3],
      theme.colors[10],
      theme.colors[1],
    ],
  };
};

const getRiskComparisonTooltipExtras = (
  isBaseline: boolean,
  isOverall: boolean,
  operationalRisk: number,
  interventionRisk: number,
  combinedRisk: number,
  lof?: number | null,
) => {
  if (isOverall) {
    return [
      {
        label: `Risk Modality`,
        value: isBaseline ? "Operational Risk" : "Intervention Risk",
        labelClassName: TOOLTIP_SEMIBOLD,
      },
      {
        label: isBaseline
          ? "Overall Operational Risk"
          : "Overall Intervention Risk",
        value: isBaseline ? operationalRisk : interventionRisk,
      },
      {
        label: "Overall Combined Risk",
        value: combinedRisk,
      },
    ];
  }
  return [
    {
      label: `Risk Modality`,
      value: isBaseline ? "Operational Risk" : "Intervention Risk",
      labelClassName: TOOLTIP_SEMIBOLD,
    },
    {
      label: "LOF (Major Event)",
      value: storageFormatters.scientificNotation(lof ?? 0),
    },
    {
      label: isBaseline
        ? "Total Major Event Risk (Operational)"
        : "Total Major Event Risk (Intervention)",
      value: isBaseline ? operationalRisk : interventionRisk,
    },
    {
      label: "Total Major Event Risk (Combined)",
      value: combinedRisk,
    },
  ];
};
